import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { motion, HTMLMotionProps } from "framer-motion"
import { cn } from "@/lib/utils"

// Define types for motion button props
type MotionButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 
  | "onDrag" | "onDragEnd" | "onDragEnter" | "onDragLeave" 
  | "onDragOver" | "onDragStart" | "onDrop" | "onAnimationStart" 
  | "onAnimationEnd" | "onAnimationIteration" | "onTransitionEnd"
>

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98]",
        outline:
          "border border-input/40 bg-background hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline active:scale-[0.98]",
        success: "bg-success-600 text-white hover:bg-success-700 active:scale-[0.98]",
        warning: "bg-warning-600 text-white hover:bg-warning-700 active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  ariaLabel?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, ariaLabel, children, ...props }, ref) => {
    // Generate accessible label
    const getAccessibleLabel = () => {
      if (ariaLabel) return ariaLabel
      if (loading) return "Loading..."
      if (typeof children === 'string') return children
      return "Button"
    }
    
    // For asChild, we need to handle loading differently
    if (asChild) {
      // We need to ensure Slot receives exactly one child
      const child = React.Children.only(children) as React.ReactElement;
      
      // Clone the child element to add our props
      const enhancedChild = React.cloneElement(child, {
        className: cn(
          buttonVariants({ variant, size, className }),
          child.props.className,
          loading && "relative"
        ),
        disabled: loading || props.disabled,
        "aria-label": getAccessibleLabel(),
        "aria-busy": loading,
        ...props
      });
      
      // Return the Slot with the enhanced child
      return <Slot ref={ref}>{enhancedChild}</Slot>;
    }
    
    // Filter out props that conflict with Framer Motion
    const safeProps = Object.fromEntries(
      Object.entries(props).filter(([key]) => 
        !["onDrag", "onDragEnd", "onDragEnter", "onDragLeave", 
          "onDragOver", "onDragStart", "onDrop", "onAnimationStart",
          "onAnimationEnd", "onAnimationIteration", "onTransitionEnd"].includes(key)
      )
    ) as MotionButtonProps;

    return (
      <motion.button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        aria-label={getAccessibleLabel()}
        aria-busy={loading}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.1 }}
        {...safeProps}
      >
        {loading && (
          <motion.div
            className="mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            aria-hidden="true"
          />
        )}
        {children}
      </motion.button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
